import * as React from 'react';
import { useMemo, useCallback } from 'react';
import * as R from 'ramda';
import { Str } from '../../localization/translationKeys';
import { css } from '@emotion/core';
import { btnStyle } from '../../forms/styles/button';
import { COLORS } from '../../globalStyle/colors';
import { useRouteMatch, Link } from 'react-router-dom';
import { useRoutes, RouteTemplates } from '../../navigation/routes';
import { useAppState } from '../../core/redux/hooks';
import DevActionView from './devPlanAction/DevActionView';
import SurveyHeader from '../survey/SurveyHeader';
import { useMsgText } from '../../localization/hooks';
import { FLEX_COLUMN, FLEX_ROW } from '../../layout/flexBox';
import { useDispatch } from 'react-redux';
import { addDevPlanActionAction } from './devPlanAction/api/addDevPlanActionAction';
import { useLoading } from '../../core/api/hooks';
import LocalBtn from '../../localization/LocalBtn';
import { ActionType } from '../../core/redux/actionType';
import { AnimatePresence, motion } from 'framer-motion';
import DevPlanDescText from './DevPlanDescText';
import { onMobileDevice, HIDE_ON_MOBILE, SHOW_ON_MOBILE } from '../../globalStyle/mediaQueries';
import Msg from '../../localization/Msg';
import DevItemHeader from './DevItemHeader';
import MobileDevPlanItemActions from './MobileDevPlanItemActions';
import { useProjIdsForUserType } from '../../navigation/hooks';
import Spacer from '../../layout/Spacer';
import LocalDiv from '../../localization/LocalDiv';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faReply } from '@fortawesome/free-solid-svg-icons';
import { Z_INDEX } from '../../globalStyle/zIndex';
import { addDevItemCommentAction } from './collaboration/api/addDevItemCommentAction';
import { useCurrentUser } from '../../auth/hooks';
import { updateDevItemCommentAction } from './collaboration/api/updateDevItemCommentAction';
import { useCurrentCollaborator, useDevPlanCollaborationSetting } from './collaboration/hooks';
import { ParticipantEmailTag } from './collaboration/constants';
import CollaboratorPageHeader from './collaboration/CollaboratorPageHeader';
import { devItemSubtitleLocal, unreadCommentCount } from './collaboration/module';
import PortalAutoLayout from '../../layout/PortalAutoLayout';
import { sendDevItemCommentNotificationAction } from './collaboration/api/sendDevItemCommentNotificationAction';
import { useMount } from '../../core/hooks/useMount';
import { setDevItemCommentsReadAction } from './collaboration/api/setDevItemCommentsReadAction';
import { sendParticipantCommentNotificationAction } from './collaboration/api/sendParticipantCommentNotificationAction';


const styles = {
  root:            css`
  `,
  headerContainer: css`
    flex-grow: 1;
  `,
  header:          css`
    margin-bottom: 40px;

    ${onMobileDevice} {
      margin-top: 35px;
      margin-bottom: 35px;
    }
  `,
  itemHeader:      css`
    margin-bottom: 15px;
  `,
  planDesc:        css`
    margin-bottom: 35px;
  `,
  actionsTitle:    css`
    color: ${COLORS.DARK_GREY};
    font-size: 12px;
    line-height: 15px;
    font-weight: bold;

    ${onMobileDevice} {
      display: block;
      margin-bottom: 10px;
    }
  `,
  horizRule:       css`
    height: 1px;
    margin: 10px 0;
    border: none;
    background-color: ${COLORS.LIGHT_GREY};
    ${HIDE_ON_MOBILE};
  `,
  addActionBtn:    [
    btnStyle.primary(COLORS.BLUE),
    css`
      display: inline-block;
      min-width: 190px;
      ${HIDE_ON_MOBILE};
    `,
  ],
  actionCards:     [
    FLEX_COLUMN,
    css`
      align-items: stretch;

      ${onMobileDevice} {
        margin-bottom: 70px;
      }
    `,
  ],
  actionCard:      css`
    margin-bottom: 35px;
  `,
  icon:            css`
    color: ${COLORS.BLUE};
    font-size: 26px;
    padding-top: 8px;
  `,
};

const itemAnimation = {
  initial:    { opacity: 0, height: 0, overflow: 'hidden' },
  animate:    { opacity: 1, height: 'auto', overflow: 'hidden' },
  exit:       { opacity: 0, height: 0, overflow: 'hidden' },
  transition: { duration: 0.6 },
};


type Props = {
  className?: string;
};


/**
 * DevPlanItem: a component that renders the dev plan item page
 *
 * PureComponent: true
 * Redux Connected: false
 */
const DevPlanItem: React.FC<Props> = props => {
  const dispatch      = useDispatch();
  const currentUser   = useCurrentUser();
  const isParticipant = useMemo(() => !!currentUser, [currentUser]);

  const routeTemplate = useMemo(
    () => isParticipant
      ? RouteTemplates.ViewPlanItem
      : RouteTemplates.CollaboratorViewPlanItem,
    [isParticipant],
  );

  const match  = useRouteMatch<{ itemId?: string }>(routeTemplate);
  const itemId = React.useMemo(() => +(match?.params.itemId ?? ''), [match]);

  const item         = useAppState(state => state.devPlan?.items?.find(planItem => itemId === planItem.devItemId));
  const stateActions = useAppState(state => state.devPlan?.actionsByItemId?.[`${itemId}`] || []);
  const actionFields = useAppState(state => state.devPlan?.fieldsAnswers?.[`${item?.devItemId}`]);

  const actions = useMemo(() => R.sort((a, b) => b.devActionId - a.devActionId, stateActions), [stateActions]);

  const projIds = useProjIdsForUserType();
  const routes  = useRoutes(projIds.participantId, projIds.projDisplayId, itemId);

  const project = useAppState(state => state.projectSummary);

  const devPlanProjectId = useMemo(() => project?.devPlanProjectId || projIds.projId, [projIds.projId, project]);

  const loading = useLoading(ActionType.AddDevPlanActionRequest);

  const title = useMsgText(Str.DevPlan);

  const onClickToAdd = React.useCallback(
    () => dispatch(addDevPlanActionAction(devPlanProjectId, item?.devItemId ?? -1)),
    [devPlanProjectId, item, dispatch],
  );

  const { DevPlan, CollaboratorDevPlan } = useRoutes(projIds.participantId, projIds.projDisplayId);

  const menuOpen = useAppState(state => state.openSideMenu);

  const locale = useAppState(state => state.locale);

  const currentCollaborator = useCurrentCollaborator();

  const collaborators = useAppState(state => state.devPlan?.collaborators?.[`${devPlanProjectId}`]);

  const currentCollaboratorEmail = useMemo(
    () => currentCollaborator?.email,
    [currentCollaborator],
  );

  const collaboratorEmails = useMemo(
    () => (collaborators ?? []).filter(c => c.accepted).map(c => c.email),
    [collaborators],
  );

  const onCommentAdded = useCallback(
    (devActionId: number, comment: string) => {
      if (!isParticipant) {
        dispatch(sendDevItemCommentNotificationAction(
          projIds.projId,
          projIds.participantId,
          itemId,
          devActionId,
          currentCollaborator?.currentProject.participantEmail || '',
          currentCollaborator?.email ?? '',
          R.without([currentCollaboratorEmail!], collaboratorEmails),
          comment,
          locale.lang.languageId,
        ));
      } else {
        dispatch(sendParticipantCommentNotificationAction(
          projIds.projId,
          projIds.participantId,
          itemId,
          devActionId,
          collaboratorEmails,
          comment,
          locale.lang.languageId,
        ));
      }
    },
    [
      collaboratorEmails,
      currentCollaborator,
      currentCollaboratorEmail,
      dispatch,
      isParticipant,
      itemId,
      locale.lang.languageId,
      projIds.participantId,
      projIds.projId,
    ],
  );


  const headerSubtitle = useMemo(
    () => devItemSubtitleLocal(locale.textDirection, currentCollaborator?.currentProject) || '',
    [currentCollaborator, locale.textDirection],
  );

  const commenterLabel = useMemo(
    () => isParticipant ? ParticipantEmailTag : (currentCollaborator?.email || ''),
    [currentCollaborator, isParticipant],
  );

  const collaborationEnabled = useDevPlanCollaborationSetting();

  const addComment = useCallback(
    (actionId: number, comment: string) => dispatch(
      addDevItemCommentAction(devPlanProjectId, projIds.participantId, itemId, actionId, commenterLabel, comment),
    ),
    [devPlanProjectId, dispatch, commenterLabel, itemId, projIds.participantId],
  );

  const updateComment = useCallback(
    (commentId: number, comment: string) => dispatch(
      updateDevItemCommentAction(devPlanProjectId, projIds.participantId, itemId, commentId, comment)),
    [devPlanProjectId, dispatch, itemId, projIds.participantId],
  );

  const comments     = useAppState(state => state.devPlan?.comments?.[`${devPlanProjectId}`]?.[`${itemId}`] ?? []);
  const commentsById = useMemo(() => R.groupBy(c => `${c.devActionId}`, comments), [comments]);

  useMount(
    () => {
      if (isParticipant && unreadCommentCount(comments) > 0) {
        dispatch(setDevItemCommentsReadAction(projIds.projId, itemId));
      }
    },
  );

  if (!item) {
    return <div className={props.className} />;
  }

  return (
    <PortalAutoLayout>
      {
        /* Header */
        isParticipant
          ? (
            <SurveyHeader title={`/ ${title}`} deadline={undefined} css={styles.header} />
          ) : (
            <>
              <CollaboratorPageHeader title={title} subtitle={headerSubtitle} />
              <Spacer sizePx={50} />
            </>
          )
      }

      <LocalDiv css={FLEX_ROW}>
        <Link
          to={isParticipant ? DevPlan : CollaboratorDevPlan}
          css={[FLEX_COLUMN, css`z-index: ${menuOpen ? 'auto' : Z_INDEX.ABOVE_SIDE_MENU}`]}
        >
          <FontAwesomeIcon icon={faReply} css={styles.icon} />
        </Link>
        <Spacer sizePx={8} css={FLEX_COLUMN} />
        <LocalDiv css={[FLEX_COLUMN, styles.headerContainer]}>
          <Msg str={Str.Devitem} css={styles.actionsTitle} />
          <DevItemHeader
            participantId={projIds.participantId}
            projectId={projIds.projId}
            item={item}
            routes={routes}
            showResourcesLink={project?.developmentPlanBusinessGoalsOn ?? false}
            isParticipant = {isParticipant}
            css={styles.itemHeader}
          />
        </LocalDiv>
      </LocalDiv>

      {
        isParticipant ? (
          <DevPlanDescText
            participantId={projIds.participantId}
            projectId={projIds.projId}
            itemId={item?.devItemId ?? -1}
            desc={item?.description}
            css={styles.planDesc}
          />
        ) : (
          <Spacer sizePx={50} />
        )
      }

      {/* Add Action */}
      <Msg str={Str.DevActions} css={styles.actionsTitle} />

      <hr css={styles.horizRule} />
      {
        isParticipant && (
          <>
            <LocalBtn str={Str.AddAction} onClick={onClickToAdd} disabled={loading} css={styles.addActionBtn} />
            <hr css={styles.horizRule} />
          </>
        )
      }

      {/* Actions */}
      <AnimatePresence css={styles.actionCards}>
        { // Actions
          actions.map(
            (action, i) => (
              <motion.div key={action.devActionId} {...itemAnimation} css={styles.actionCard}>
                <DevActionView
                  routes={routes}
                  itemTitle={item?.title || ''}
                  actionNumber={actions.length - i}
                  action={action}
                  fieldsAnswers={actionFields?.[action.devActionId] ?? []}
                  collaborationEnabled={collaborationEnabled}
                  isParticipant={isParticipant}
                  comments={commentsById[`${action.devActionId}`] ?? []}
                  addComment={comment => addComment(action.devActionId, comment)}
                  onCommentAdded={comment => onCommentAdded(action.devActionId, comment)}
                  updateComment={updateComment}
                  devItemCompetencyId={item?.devItemCompetencyId}
                />
              </motion.div>
            ),
          )
        }
      </AnimatePresence>

      <Spacer sizePx={45} css={SHOW_ON_MOBILE} />

      {
        isParticipant && (
          <MobileDevPlanItemActions routes={routes} onClickToAdd={onClickToAdd} />
        )
      }
    </PortalAutoLayout>
  );
};

export default DevPlanItem;
